import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Sparkles, AlertCircle, Plus } from 'lucide-react';
import IgnitingLoader from './IgnitingLoader';
import { useAuth, formatApiError } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AskEnfernoChat — Shared chat UI used both inside the global drawer and
 * the dedicated /coach page. Persists conversation server-side; passes
 * session_id back and forth so multi-turn works across page reloads.
 *
 * Props:
 *   variant: 'drawer' | 'page' — controls height + padding
 *   sessionId: optional starting session id (e.g. resumed from a list)
 *   onSessionChange: callback when session id is created/changed
 */
const AskEnfernoChat = ({ variant = 'page', sessionId: initialSessionId = null, onSessionChange }) => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const scrollRef = useRef(null);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/usage`);
      setUsage(data);
    } catch { /* ignore */ }
  }, []);

  const loadSession = useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/ai/chat/sessions/${id}`);
      setMessages(data.messages || []);
    } catch (err) {
      console.warn('Failed to load chat session', err?.message);
    }
  }, []);

  useEffect(() => {
    if (user) loadUsage();
  }, [user, loadUsage]);

  useEffect(() => {
    if (initialSessionId) {
      setSessionId(initialSessionId);
      loadSession(initialSessionId);
    }
  }, [initialSessionId, loadSession]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setError('');

    // Optimistic append
    const userTurn = { role: 'user', content: text };
    setMessages((m) => [...m, userTurn]);

    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, {
        message: text,
        session_id: sessionId,
      });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        onSessionChange?.(data.session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      loadUsage();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
      // Roll back the optimistic user turn so they can retry without dupe
      setMessages((m) => m.slice(0, -1));
      setInput(text); // restore so they can retry
    } finally {
      setBusy(false);
    }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setError('');
    onSessionChange?.(null);
  };

  const containerH = variant === 'drawer' ? 'h-[60vh]' : 'h-[70vh]';
  const empty = messages.length === 0 && !busy;

  if (!user) {
    return (
      <div className="text-center py-16 text-enf-text-secondary">
        <Sparkles className="w-8 h-8 mx-auto mb-3 text-enf-accent-fg" />
        <p>Sign in to chat with your AI coach.</p>
      </div>
    );
  }

  const overLimit = usage && !usage.is_admin && usage.chat_messages_used >= (usage.chat_limit || 0);

  return (
    <div className={`flex flex-col ${containerH} bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden`} data-testid="ask-enferno-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-enf-surface-border bg-enf-surface-mid">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-enf-accent flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-enf-text-primary" />
          </div>
          <div>
            <div className="text-sm font-semibold">Ask Enferno</div>
            <div className="text-[10px] uppercase tracking-widest text-enf-text-muted">AI training coach</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {usage && (
            <div className="text-[10px] uppercase tracking-widest text-enf-text-muted" title="Resets monthly">
              {usage.is_admin ? 'Unlimited' : `${usage.chat_messages_used}/${usage.chat_limit}`}
            </div>
          )}
          <button onClick={newSession} className="text-xs text-enf-text-secondary hover:text-enf-text-primary flex items-center gap-1" data-testid="chat-new-session">
            <Plus className="w-3 h-3" /> New
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {empty && (
          <div className="text-center py-10">
            <div className="font-display text-3xl tracking-wide leading-none mb-3">Bring the heat.</div>
            <div className="text-sm text-enf-text-secondary max-w-md mx-auto mb-6">
              Ask about technique, programming, recovery, nutrition — anything training.
              I know your profile, your injuries, and your recent sessions.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
              {[
                "How should I program around my shoulder injury?",
                "What should I eat post-workout for recovery?",
                "I keep failing my bench at 225. What now?",
                "How do I balance BJJ days with lifting?",
              ].map((q, i) => (
                <button key={i} onClick={() => setInput(q)}
                  className="text-left text-xs text-enf-text-secondary hover:text-enf-text-primary bg-enf-surface-mid hover:bg-enf-surface-mid border border-enf-surface-border rounded-md px-3 py-2 transition-colors"
                  data-testid={`chat-suggestion-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-enf-accent text-enf-text-primary rounded-br-md'
                : 'bg-enf-surface-mid text-enf-text-primary border border-enf-surface-border rounded-bl-md'
            }`}>
              {m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start">
            <div className="bg-enf-surface-mid border border-enf-surface-border rounded-2xl rounded-bl-md px-4 py-2">
              <IgnitingLoader label="Thinking..." size="sm" />
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/40 rounded-md text-sm text-enf-accent-fg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={send} className="border-t border-enf-surface-border bg-enf-surface-mid px-3 py-3 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={overLimit ? 'Your chat refreshes at the start of next month — the rest of Enferno stays open.' : 'Ask anything training...'}
          rows={1}
          disabled={overLimit}
          className="flex-1 resize-none px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="chat-input"
        />
        <button type="submit" disabled={busy || !input.trim() || overLimit}
          className="px-3 py-2 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold transition-colors flex-shrink-0"
          data-testid="chat-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

export default AskEnfernoChat;
